import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from '../../store/store';
import { styles } from './cashTransactions.styles';
import { fetchCashTrnsByInhand, fetchCashBankTrnIds, fetchCashTrnsByTrnId, setCurrentFilter, toggleTransactionSelection, changeCashInHand, clearSelectedTransactions } from '../../store/slices/cashTransactionsSlice';
import {
    Box,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    Checkbox,
    Alert,
    Snackbar,
    CircularProgress,
    TextField,
    MenuItem,
    Select,
    SelectChangeEvent,
    FormControl,
    InputLabel,
} from '@mui/material';
// import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';
import * as yup from 'yup';


const cashTransactionValidationSchema = yup.object({
    cashTrnDate: yup.date().required('Cash Transaction Date is required'),
    bankTrnId: yup.string().trim().required('Bank Transaction ID cannot be empty'),
    selectedTransactions: yup.array().min(1, 'Please select at least one transaction'),
});

const AdminCashComponent: React.FC = () => {

    const [cashTrnDate, setCashTrnDate] = useState<Date>(new Date());
    const [bankTrnId, setBankTrnId] = useState<string>('');
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({ open: false, message: '', severity: 'success' });
    const [validationErrors, setValidationErrors] = useState<{
        cashTrnDate?: string;
        bankTrnId?: string;
        selectedTransactions?: string;
    }>({})


    const dispatch = useDispatch<AppDispatch>();
    const cashInHandResponse = useSelector((state: RootState) => state.cashTransactions.transactions);
    const bankTrnIds = useSelector((state: RootState) => state.cashTransactions.cashBankTrnIds);
    const filterValueState = useSelector((state: RootState) => state.cashTransactions.currentFilter);
    const selectedTransactions = useSelector((state: RootState) => state.cashTransactions.selectedTransactions);
    const { loading, error } = useSelector((state: RootState) => state.cashTransactions);

    const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    useEffect(() => {
        if (filterValueState !== 'cashInHand' && selectedTransactions.length > 0) {
            const selectedTrns = cashInHandResponse.filter(trn =>
                selectedTransactions.includes(trn.id)
            );
            if (selectedTrns.length > 0 && selectedTrns[0].cashBankTrnId) {
                setBankTrnId(selectedTrns[0].cashBankTrnId);
                if (selectedTrns[0].cashTrnDate) {
                    try {
                        setCashTrnDate(new Date(selectedTrns[0].cashTrnDate));
                    } catch (error) {
                        console.error('Invalid date format:', error);
                        showSnackbar('Invalid date format in transaction data', 'warning');
                    }
                }
            }
        } else if (selectedTransactions.length === 0) {
            // Reset form when all selections are cleared
            // if (filterValueState === 'cashInHand') {
                setBankTrnId('');
                setCashTrnDate(new Date());
            // }
        }
    }, [selectedTransactions, filterValueState, cashInHandResponse]);

    // This use effect is used for initial data fetch and fetch data when switching between tabs.
    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                await dispatch(fetchCashBankTrnIds('BANK')).unwrap();

                if (filterValueState === 'cashInHand') {
                    await dispatch(fetchCashTrnsByInhand('ADMIN')).unwrap();
                } else {
                    await dispatch(fetchCashTrnsByTrnId(filterValueState)).unwrap();
                }
            } catch (error: any) {
                console.error('Error fetching initial data:', error);
                showSnackbar(
                    error?.message || 'Failed to load transaction data. Please refresh the page.',
                    'error'
                );
            }
        };

        fetchInitialData();
    }, [dispatch, filterValueState]);

    // Show error from Redux state
    useEffect(() => {
        if (error) {
            showSnackbar(error, 'error');
        }
    }, [error]);

    // Handle filter change is used for channging filter and dispatch api action on selected filter change. (setCurrentFilter is redux state which persists state when switching tabs)
    const handleFilterChange = async (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        dispatch(setCurrentFilter(value));
        dispatch(clearSelectedTransactions()); // Clear selections when changing filter

        // Reset form fields
        setBankTrnId('');
        setCashTrnDate(new Date());
        setValidationErrors({});

        try {
            if (value === 'cashInHand') {
                await dispatch(fetchCashTrnsByInhand('ADMIN')).unwrap();
            } else {
                await dispatch(fetchCashTrnsByTrnId(value)).unwrap();
            }
        } catch (error: any) {
            console.error('Error changing filter:', error);
            showSnackbar(
                error?.message || 'Failed to load filtered transactions',
                'error'
            );
        }
    };

    const handleCheckBoxChange = (transactionId: number) => {
        setValidationErrors({});
        dispatch(toggleTransactionSelection(transactionId));
        if (validationErrors.selectedTransactions) {
            setValidationErrors(prev => ({ ...prev, selectedTransactions: undefined }));
        } 
    }

    const handleSubmit = async () => {
        if (filterValueState === 'cashInHand') {
            try {
                await cashTransactionValidationSchema.validate(
                    { cashTrnDate, bankTrnId, selectedTransactions },
                    { abortEarly: false }
                );
                setValidationErrors({});
            } catch (error) {
                if (error instanceof yup.ValidationError) {
                    const errors = error.inner.reduce((acc: any, err: any) => {
                        if (err.path) acc[err.path as keyof typeof validationErrors] = err.message;
                        return acc;
                    }, {});
                    setValidationErrors(errors);
                    showSnackbar(error.inner[0]?.message || 'Validation failed', 'warning');
                    return;
                }
            }
            const payloadForSubmit = {
                cashInHand: 'BANK',
                cashBankTrnId: bankTrnId,
                cashTrnDate: format(cashTrnDate, "yyyy-MM-dd'T'00:00:00"),
                transactionIds: selectedTransactions,
            };
            try {
                await dispatch(changeCashInHand(payloadForSubmit)).unwrap();
                // Only fetch after successful submission
                await Promise.all([
                    dispatch(fetchCashTrnsByInhand('ADMIN')).unwrap(),
                    dispatch(fetchCashBankTrnIds('BANK')).unwrap(),
                ]);
                dispatch(clearSelectedTransactions());
                // Reset form
                setBankTrnId('');
                setCashTrnDate(new Date());
                setValidationErrors({});

                showSnackbar(
                    `Successfully moved ${selectedTransactions.length} transaction(s) to BANK`,
                    'success'
                );
            } catch (error) {
                console.error('Failed to change cash in hand:', error);
            }
        } else {
            const selectedTrns = cashInHandResponse.filter(trn =>
                selectedTransactions.includes(trn.id)
            );
            if (selectedTrns.length > 0) {

                if (!selectedTrns[0].cashBankTrnId) {
                    showSnackbar('Selected transaction is missing Bank Transaction ID', 'error');
                    return;
                }
                const removeBankTrnPayload = {
                    cashInHand: 'ADMIN',
                    cashBankTrnId: selectedTrns[0].cashBankTrnId,
                    cashTrnDate: selectedTrns[0].cashTrnDate,
                    transactionIds: selectedTransactions,
                }
                try {
                    await dispatch(changeCashInHand(removeBankTrnPayload)).unwrap();
                    await Promise.all([
                        dispatch(fetchCashTrnsByTrnId(filterValueState)).unwrap(),
                        dispatch(fetchCashBankTrnIds('BANK')).unwrap(),
                        dispatch(fetchCashTrnsByInhand('ADMIN')).unwrap(),
                    ]);
                    dispatch(clearSelectedTransactions());
                    setBankTrnId("");
                    setCashTrnDate(new Date());
                    setValidationErrors({});
                    dispatch(setCurrentFilter('cashInHand'));
                    showSnackbar(
                        `Successfully removed ${selectedTransactions.length} transaction(s) from BANK`,
                        'success'
                    );
                }
                catch (error) {
                    console.error('Failed to change cash in hand:', error);
                    showSnackbar(
                        'Failed to update transactions. Please try again.', 'error'
                    );
                }
            }
        }
    };

    const totalAmount = cashInHandResponse
        .filter(transaction => selectedTransactions.includes(transaction.id))
        .reduce((sum, transaction) => sum + transaction.amount, 0);
    const totalFilteredAmount = cashInHandResponse.reduce((sum, transaction) => sum + transaction.amount, 0);
    return (
        <Box>
            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel
                        id="filter-select-label"
                        sx={{ color: '#ffffff' }}
                    >
                        Filter By
                    </InputLabel>
                    <Select
                        labelId="filter-select-label"
                        label="Filter By"
                        value={filterValueState}
                        onChange={handleFilterChange}
                        sx={styles.customSelect}
                        MenuProps={{
                            PaperProps: {sx: styles.selectMenuPaper},
                        }}
                    >
                        <MenuItem value="cashInHand">Cash In Hand</MenuItem>
                        {bankTrnIds && bankTrnIds.length > 0 && bankTrnIds.map((trnId: any) => (
                            <MenuItem key={trnId} value={trnId}>
                                Bank Trn - {trnId}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography sx={{  fontWeight: 'bold' ,bgcolor:"#0d47a1", padding:"5px", borderRadius:"5px", color:"#e3f2fd"}}>
                    Total : {totalFilteredAmount.toFixed(2)}
                </Typography>
                {/* {selectedTransactions.length > 0 && ( */}
                    <Box
                        component={Paper}
                        elevation={3}
                        sx={styles.formBox}

                    >
                        <Typography sx={styles.totalAmountText}>Total Amount : <span style={{color:"orange"}}>{totalAmount.toFixed(2)}</span></Typography>
                        <Box sx={styles.formFieldsBox}>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    label="Cash Trn Date"
                                    value={cashTrnDate}
                                    disabled={filterValueState !== 'cashInHand'}
                                    format="dd/MM/yyyy"
                                    onChange={(newValue) => {
                                        if (newValue) {
                                            setCashTrnDate(newValue)
                                             if (validationErrors.cashTrnDate) {
                                                setValidationErrors(prev => ({ ...prev, cashTrnDate: undefined }));
                                            }
                                        }
                                    }}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            sx: styles.dateTimePicker,
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                            <TextField
                                label="Bank Trn ID"
                                value={bankTrnId}
                                disabled={filterValueState !== 'cashInHand'}
                                onChange={(e) => {setBankTrnId(e.target.value);
                                    if (validationErrors.bankTrnId) {
                                        setValidationErrors(prev => ({ ...prev, bankTrnId: undefined }));
                                    }
                                }}
                                size="small"
                                error={!!validationErrors.bankTrnId}
                                // helperText={validationErrors.bankTrnId}
                                sx={styles.textField}
                            />

                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disabled={selectedTransactions.length === 0}
                                sx={styles.submitButton}
                            >
                                {filterValueState === 'cashInHand' ? 'Submit' : 'Remove'}
                            </Button>
                        </Box>
                    </Box>
                {/* // )} */}
            </Box>
            {/* Transactions Table */}
            <TableContainer component={Paper} sx={styles.tableContainer}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={styles.tableCellWithWidth('150px')}>Cash Trn Id</TableCell>
                            <TableCell sx={styles.tableCellWithWidth('100px')}>Trip Id</TableCell>
                            <TableCell sx={styles.tableCellWithWidth('150px')}>Amount</TableCell>
                            <TableCell sx={styles.tableCellWithWidth('200px')}>Trn Date</TableCell>
                            <TableCell sx={styles.tableCellWithWidth('200px')}>Bank Trn Id</TableCell>
                            <TableCell padding="checkbox" sx={{ width: '50px' }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {cashInHandResponse.map((transaction: any) => (
                            <TableRow key={transaction.id}>
                                <TableCell sx={styles.tableCell}>{transaction.id}</TableCell>
                                <TableCell sx={styles.tableCell}>{transaction.tripCode}</TableCell>
                                <TableCell sx={styles.tableCell}>{transaction.amount.toFixed(2)}</TableCell>
                                <TableCell sx={styles.tableCell}>
                                    {transaction.cashTrnDate && !isNaN(new Date(transaction.cashTrnDate).getTime())
                                        ? format(new Date(transaction.cashTrnDate), 'dd MMM yyyy')
                                        : '-'
                                    }
                                </TableCell>
                                <TableCell sx={styles.tableCell}>{transaction.cashBankTrnId || '-'}</TableCell>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selectedTransactions.includes(transaction.id)}
                                        onChange={() => handleCheckBoxChange(transaction.id)}
                                        sx={styles.checkbox}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box >
    )
}

export default AdminCashComponent;